import type { ConversationDetail, ConversationSummary } from '../types/conversation.js';

const LIST_KEY = 'nexvill.conversations.list';
// `v2` because the entry shape gained a completeness marker (#370). Entries
// written by an earlier build carry no marker, and there is no way to tell a
// complete one from a truncated one after the fact — a wrong guess silently
// exports a partial transcript. A new prefix retires them instead of guessing.
const DETAIL_PREFIX = 'nexvill.conversations.detail.v2.';
const BOOTSTRAP_KEY = 'nexvill.bootstrap.session';

export const INITIAL_CONVERSATION_MESSAGE_LIMIT = 50;

export type BootstrapSessionCache = {
  userId: string;
  conversations: ConversationSummary[];
  initialConversationId: string | null;
  initialConversation: ConversationDetail | null;
  cachedAt: number;
};

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

function readJson<T>(key: string): T | null {
  if (!canUseStorage()) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private mode — ignore.
  }
}

export function readConversationListCache(): ConversationSummary[] | null {
  return readJson<ConversationSummary[]>(LIST_KEY);
}

export function writeConversationListCache(conversations: ConversationSummary[]): void {
  writeJson(LIST_KEY, conversations);
}

/**
 * A cached conversation detail, tagged with whether it is the WHOLE history.
 *
 * The tag exists because the cache is keyed on conversation id alone, while the
 * fetches that fill it are no longer all the same size (#370): opening a
 * conversation now asks for the newest {@link INITIAL_CONVERSATION_MESSAGE_LIMIT}
 * messages. Without the tag, a caller that needs everything — export is the one
 * that matters — cannot tell a full entry from a 50-message tail, and silently
 * writes out a truncated transcript.
 */
export type ConversationDetailCacheEntry = {
  detail: ConversationDetail;
  /** True only when the entry holds every message in the conversation. */
  isComplete: boolean;
};

export function readConversationDetailCache(
  conversationId: string
): ConversationDetailCacheEntry | null {
  const entry = readJson<ConversationDetailCacheEntry>(`${DETAIL_PREFIX}${conversationId}`);
  if (!entry || typeof entry !== 'object' || !entry.detail) {
    return null;
  }
  // Anything not explicitly marked complete is treated as partial. Erring the
  // other way loses messages; erring this way costs one refetch.
  return { detail: entry.detail, isComplete: entry.isComplete === true };
}

export function writeConversationDetailCache(
  conversationId: string,
  detail: ConversationDetail,
  options: { isComplete: boolean }
): void {
  writeJson(`${DETAIL_PREFIX}${conversationId}`, {
    detail,
    isComplete: options.isComplete,
  } satisfies ConversationDetailCacheEntry);
}

export function readBootstrapSessionCache(): BootstrapSessionCache | null {
  return readJson<BootstrapSessionCache>(BOOTSTRAP_KEY);
}

export function writeBootstrapSessionCache(cache: BootstrapSessionCache): void {
  writeJson(BOOTSTRAP_KEY, cache);
}

export function clearBootstrapSessionCacheForUser(userId: string): void {
  const existing = readBootstrapSessionCache();
  if (existing?.userId === userId) {
    if (canUseStorage()) {
      window.sessionStorage.removeItem(BOOTSTRAP_KEY);
    }
  }
}
